import React, { useEffect, useState, useRef } from "react";
import companyData from "./tableData.json";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchReservationsPrepaidBookingsReport,
  initializeReservationsPrepaidBookingsReportScreen,
  setDeleteConfirmationPopupVisibility,
  exportReservationsPrepaidBookingsReport,
  setReservationPrepaidBookingFilters,
  clearReservationPrepaidBookingFilters,
  setPrepaidBookingTableColumnFilters,
} from "../../../redux/actions/Reservations/ReservationsPrepaidBookingsReport";
import { fetchLocationDD } from "../../../redux/actions/location";
import { fetchRateAdminNetworkDD } from "../../../redux/actions/RateAdmin/rateadminRates";
import ReservationsPrepaidBookingsReport from "../../../component/Reservations/ReservationsPrepaidBookingsReport";
import { generatePath } from "react-router-dom";
import { loadingShow } from "../../../services/PostAPI";
import { APP_URLS } from "../../../utils/constant";

// Module-level variables to persist filters across component remounts
let cachedFilters = null;
let cachedColumnFilters = null;

function ReservationsPrepaidBookingsReportScreen(props) {
  const {
    fetchReservationsPrepaidBookingsReport,
    initReservationsPrepaidBookingsReportScreen,
    showPageLevelLoader,
    ReservationsPrepaidBookingsReportListview,
    paginationInfo,
    screenPermissions,
    exportReservationsPrepaidBookingsReport,
    isReservationsPrepaidBookingsReportExportError,
    isReservationsPrepaidBookingsReportExportSuccess,
    isLoading,
    fetchLocationDD,
    fetchRateAdminNetworkDD,
    RateNetworkDD,
    locationDD,
    setReservationPrepaidBookingFilters,
    clearReservationPrepaidBookingFilters,
    reservationPrepaidBookingsFilters,
    tableColumnFilters,
    setPrepaidBookingTableColumnFilters,
  } = props;
  const navigate = useNavigate();
  const location = useLocation();
  
  // Capture initial navigation state in a ref so it doesn't change
  const isComingBackRef = useRef(location.state?.fromPrepaidBookings === true);
  
  // Wrapper that updates module-level cache before dispatching to Redux
  const setFiltersWithCache = (filters) => {
    cachedFilters = filters;
    setReservationPrepaidBookingFilters(filters);
  };
  
  // Wrapper that updates module-level cache for column filters before dispatching to Redux
  const setColumnFiltersWithCache = (filters) => {
    cachedColumnFilters = filters;
    setPrepaidBookingTableColumnFilters(filters);
  };

  const callAPIasync = async () => {
    await fetchRateAdminNetworkDD();
    await fetchLocationDD();
  };

  useEffect(() => {
    callAPIasync();
  }, []);
  
  // Helper to convert tableColumnFilters to the array format expected by the service
  const getColumnFiltersArray = (columnFilters) => {
    if (!columnFilters || Object.keys(columnFilters).length === 0) return [];
    return Object.keys(columnFilters)
      .filter(key => columnFilters[key])
      .map(key => ({
        filterField: key,
        matchStrategy: "CONTAINS",
        query: columnFilters[key],
      }));
  };
  
  // SINGLE initialization effect that handles both cases
  useEffect(() => {
    const isComingBack = isComingBackRef.current;
    
    if (isComingBack) {
      // Coming back from detail view - use cached filters and reload data
      const headerFilters = cachedFilters || reservationPrepaidBookingsFilters;
      const columnFilters = cachedColumnFilters || tableColumnFilters;
      const columnFiltersArray = getColumnFiltersArray(columnFilters);
      
      if (headerFilters?.location?.length > 0 && headerFilters?.network?.length > 0) {
        // Use additinalFilter to match the expected payload structure in the service
        fetchReservationsPrepaidBookingsReport({
          pageNumber: paginationInfo?.pageNumber || 0,
          pageSize: paginationInfo?.pageSize || 10,
          sortField: "id",
          userId: localStorage.getItem("userId"),
          additinalFilter: headerFilters,
          filters: columnFiltersArray,
        });
      }
      // Clear navigation state
      navigate(location.pathname, { replace: true, state: {} });
    } else {
      // Fresh load - initialize screen
      initReservationsPrepaidBookingsReportScreen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLazyData = (props) => {
    const filters = props.additinalFilter ?? cachedFilters ?? reservationPrepaidBookingsFilters;
    
    const hasLocation =
      filters?.location &&
      Array.isArray(filters.location) &&
      filters.location.length > 0;
    const hasNetwork =
      filters?.network &&
      Array.isArray(filters.network) &&
      filters.network.length > 0;

    if (!hasLocation || !hasNetwork) {
      return;
    }

    // Include column filters from props (from EzTable) or from cached/Redux state
    const columnFiltersArray = props.filters || getColumnFiltersArray(cachedColumnFilters || tableColumnFilters);

    let newProps = {
      ...props,
      userId: localStorage.getItem("userId"),
      ...filters,
      filters: columnFiltersArray,
    };
    fetchReservationsPrepaidBookingsReport(newProps);
  };
  
  // Wrapper for export that uses cached filters
  const handleExportWithFilters = (payload) => {
    const filters = payload ?? cachedFilters ?? reservationPrepaidBookingsFilters;
    exportReservationsPrepaidBookingsReport(filters);
  };
  
  const handleCreateReservationsPrepaidBookingsReport = () => {
    navigate(APP_URLS.RESERVATIONS_PREPAIDBOOKINGSREPORT.CREATE);
  };
  const editReservationsPrepaidBookingsReportRoute = (id) => {
    const path = generatePath(
      APP_URLS.RESERVATIONS_PREPAIDBOOKINGSREPORT.EDIT,
      {
        id,
      },
    );
    navigate(path);
  };
  
  // View reservation route - pass state indicating we came from Prepaid Bookings
  const viewReservationRoute = (id) => {
    navigate(`/view-reservations/${id}/view`, { state: { fromPrepaidBookings: true } });
  };
  
  const ReservationsPrepaidBookingsReportProps = {
    handleCreateReservationsPrepaidBookingsReport,
    exportReservationsPrepaidBookingsReport: handleExportWithFilters,
    isReservationsPrepaidBookingsReportExportError,
    isReservationsPrepaidBookingsReportExportSuccess,
    editReservationsPrepaidBookingsReportRoute,
    viewReservationRoute,
    screenPermissions,
    setDeleteConfirmationPopupVisibility,
    fetchReservationsPrepaidBookingsReports: loadLazyData,
    tableData: {
      columns: ReservationsPrepaidBookingsReportListview?.columns,
      rows: ReservationsPrepaidBookingsReportListview?.rows,
    },
    paginationInfo: {
      pageSize: paginationInfo?.pageSize,
      pageNumber: paginationInfo?.pageNumber,
      totalPages: paginationInfo?.totalPages,
      totalRecords: paginationInfo?.totalRecords,
    },
    isLoading,
    RateNetworkDD,
    locationDD,
    setReservationPrepaidBookingFilters: setFiltersWithCache,
    clearReservationPrepaidBookingFilters,
    reservationPrepaidBookingsFilters,
    tableColumnFilters: cachedColumnFilters || tableColumnFilters,
    setPrepaidBookingTableColumnFilters: setColumnFiltersWithCache,
    isComingBack: isComingBackRef.current,
  };

  if (showPageLevelLoader) {
    loadingShow("block");
    return null;
  }
  loadingShow("none");
  return (
    <div>
      <ReservationsPrepaidBookingsReport
        {...ReservationsPrepaidBookingsReportProps}
      />
    </div>
  );
}

const mapDispatchToProps = (dispatch) => {
  return {
    initReservationsPrepaidBookingsReportScreen: () =>
      dispatch(initializeReservationsPrepaidBookingsReportScreen()),
    fetchReservationsPrepaidBookingsReport: (payload) =>
      dispatch(fetchReservationsPrepaidBookingsReport(payload)),
    setDeleteConfirmationPopupVisibility: (payload) =>
      dispatch(setDeleteConfirmationPopupVisibility(payload)),
    fetchRateAdminNetworkDD: (payload) =>
      dispatch(fetchRateAdminNetworkDD(payload)),
    fetchLocationDD: (payload) => dispatch(fetchLocationDD(payload)),
    exportReservationsPrepaidBookingsReport: (payload) =>
      dispatch(exportReservationsPrepaidBookingsReport(payload)),
    setReservationPrepaidBookingFilters: (filters) =>
      dispatch(setReservationPrepaidBookingFilters(filters)),
    clearReservationPrepaidBookingFilters: () =>
      dispatch(clearReservationPrepaidBookingFilters()),
    setPrepaidBookingTableColumnFilters: (filters) =>
      dispatch(setPrepaidBookingTableColumnFilters(filters)),
  };
};


const mapStateToProps = (state, ownProps) => {
  return {
    isReservationsPrepaidBookingsReportCreateSuccess:
      selectFromReservationsPrepaidBookingsReportReducer(
        state,
        "isReservationsPrepaidBookingsReportCreateSuccess",
      ),
    ReservationsPrepaidBookingsReportListview:
      selectFromReservationsPrepaidBookingsReportReducer(
        state,
        "ReservationsPrepaidBookingsReportListview",
      ),
    showPageLevelLoader: selectFromReservationsPrepaidBookingsReportReducer(
      state,
      "showPageLevelLoader",
    ),
    paginationInfo: selectFromReservationsPrepaidBookingsReportReducer(
      state,
      "paginationInfo",
    ),
    isPageLevelError: selectFromReservationsPrepaidBookingsReportReducer(
      state,
      "isPageLevelError",
    ),
    isFetchingGridData: selectFromReservationsPrepaidBookingsReportReducer(
      state,
      "isFetchingGridData",
    ),
    RateNetworkDD: selectFromRateAdminRatesReducer(state, "RateNetworkDD"),
    locationDD: selectFromLocationReducer(state, "locationDD"),
    isReservationsPrepaidBookingsReportExportError:
      selectFromReservationsPrepaidBookingsReportReducer(
        state,
        "isReservationsPrepaidBookingsReportExportError",
      ),
    isReservationsPrepaidBookingsReportExportSuccess:
      selectFromReservationsPrepaidBookingsReportReducer(
        state,
        "isReservationsPrepaidBookingsReportExportSuccess",
      ),
    reservationPrepaidBookingsFilters:
      selectFromReservationsPrepaidBookingsReportReducer(
        state,
        "reservationPrepaidBookingsFilters",
      ),
    tableColumnFilters:
      selectFromReservationsPrepaidBookingsReportReducer(
        state,
        "tableColumnFilters",
      ),
    ...ownProps,
  };
};

const selectFromReservationsPrepaidBookingsReportReducer = (state, path) => {
  return selectReservationsPrepaidBookingsReportReducer(state)[path];
};

const selectReservationsPrepaidBookingsReportReducer = ({
  ReservationsPrepaidBookingsReportReducer,
}) => {
  return ReservationsPrepaidBookingsReportReducer;
};

// For Network
const selectFromRateAdminRatesReducer = (state, path) => {
  return selectRateAdminRatesReducer(state)[path];
};

const selectRateAdminRatesReducer = ({ rateadminRatesReducer }) => {
  return rateadminRatesReducer;
};
//Location
const selectFromLocationReducer = (state, path) => {
  return selectLocationReducer(state)[path];
};
const selectLocationReducer = ({ locationReducer }) => {
  return locationReducer;
};
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ReservationsPrepaidBookingsReportScreen);
